import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types/user';

interface AuthMiddlewareProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requireOnboarding?: boolean;
}

const AuthMiddleware: React.FC<AuthMiddlewareProps> = ({
  children,
  allowedRoles,
  requireOnboarding = true
}) => {
  const { user, isLoaded, isSignedIn, getUserRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoaded) return;

    // Handle authentication
    if (!isSignedIn) {
      navigate('/sign-in', { state: { from: location } });
      return;
    }

    if (!user) return;

    const userRole = getUserRole();
    const hasCompletedOnboarding = user.unsafeMetadata?.onboardedAt;

    // Handle onboarding
    if (requireOnboarding && !hasCompletedOnboarding && location.pathname !== '/onboarding') {
      navigate('/onboarding', { state: { from: location } });
      return;
    }

    // Handle role-based access
    if (allowedRoles && !allowedRoles.includes(userRole)) {
      navigate('/unauthorized', { state: { from: location } });
      return;
    }
  }, [isLoaded, isSignedIn, user, location, navigate, allowedRoles, requireOnboarding, getUserRole]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthMiddleware; 